'use client';

import React, { useState, useReducer, useEffect } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { useTheme } from '@/core/providers/ThemeProvider';
import { X } from 'lucide-react';
import { showToast } from '@/shared/utils/toast';
import { YourBusinessHeader } from './YourBusinessHeader';
import { Skeleton } from '@/shared/components/skeletons';
import { useGetOrganizationDetailsQuery, useUpdateOrganizationMutation } from '@/api/features/organizations';
import { isRtkQueryError } from '@voley/shared-types/api';
import { useAppSelector } from '@/core/store/hooks';
import { useSecureAuthContext } from '@/core/auth/services';

import {
  BusinessHours,
  DEFAULT_BUSINESS_HOURS,
  convertToBackendFormat,
  convertToFrontendFormat,
} from './businessHoursUtils';
import { BusinessInfoSection } from './BusinessInfoSection';
import { TimezoneSection } from './TimezoneSection';
import { BusinessHoursSection } from './BusinessHoursSection';
import { AfterHoursSection } from './AfterHoursSection';

interface BusinessInfoState {
  companyName: string;
  companyEmail: string;
  companyWebsite: string;
  companyAddress: string;
  brandColor: string;
  timezone: string;
}

const DEFAULT_BIZ_INFO: BusinessInfoState = {
  companyName: '', companyEmail: '', companyWebsite: '', companyAddress: '',
  brandColor: '#22577A', timezone: 'America/Denver',
};

const YourBusiness: React.FC = () => {
  const { theme } = useTheme();
  const { user: authUser } = useSecureAuthContext();

  const userProfile = useAppSelector(state => state.userProfile.session);
  const organizationId = userProfile?.organizationId || authUser?.organizationId;

  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
    error: organizationError
  } = useGetOrganizationDetailsQuery(organizationId ?? '', { skip: !organizationId });

  const [updateOrganization, { isLoading: isUpdatingOrganization }] = useUpdateOrganizationMutation();

  const [bizInfo, setBizInfo] = useReducer(
    (s: BusinessInfoState, a: Partial<BusinessInfoState>) => ({ ...s, ...a }),
    DEFAULT_BIZ_INFO,
  );
  const [businessHours, setBusinessHours] = useState<BusinessHours>(DEFAULT_BUSINESS_HOURS);
  const [autoReply, setAutoReply] = useState({ enabled: false, message: "Thanks for reaching out! We're currently outside of business hours but will get back to you as soon as possible during our next business day." });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (organizationData) {
      queueMicrotask(() => {
        setBizInfo({
          companyName: organizationData.name || '',
          companyEmail: organizationData.email || '',
          companyWebsite: organizationData.website || '',
          companyAddress: organizationData.address || '',
          brandColor: organizationData.brandColor || '#22577A',
          timezone: organizationData.timezone || 'America/Denver',
        });
      });

      if (organizationData.businessHours) {
        try {
          const savedBusinessHours = JSON.parse(organizationData.businessHours);
          const firstDay = Object.values(savedBusinessHours)[0] as Record<string, unknown>;
          if (firstDay && firstDay.startTime) {
            queueMicrotask(() => {
              setBusinessHours(savedBusinessHours);
            });
          } else {
            queueMicrotask(() => {
              setBusinessHours(convertToFrontendFormat(savedBusinessHours));
            });
          }
        } catch (error) {
          reduxLogger.error('Error parsing business hours:', error);
        }
      }

      if (organizationData.autoReplyEnabled !== undefined || organizationData.autoReplyMessage) {
        queueMicrotask(() => {
          setAutoReply(prev => ({
            enabled: organizationData.autoReplyEnabled ?? prev.enabled,
            message: organizationData.autoReplyMessage ?? prev.message,
          }));
        });
      }
    }
  }, [organizationData]);

  const handleToggleDay = (day: string) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: { ...prev[day], isOpen: !prev[day].isOpen }
    }));
  };

  const handleTimeChange = (day: string, timeType: 'startTime' | 'endTime', value: string) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [timeType]: value }
    }));
  };

  const handleSave = async () => {
    if (!organizationId) {
      reduxLogger.error('No organization ID available');
      return;
    }

    setFieldErrors({});

    try {
      const backendBusinessHours = convertToBackendFormat(businessHours);
      const updateData = {
        name: bizInfo.companyName,
        email: bizInfo.companyEmail || null,
        website: bizInfo.companyWebsite || null,
        address: bizInfo.companyAddress || null,
        timezone: bizInfo.timezone,
        businessHours: JSON.stringify(backendBusinessHours),
        autoReplyEnabled: autoReply.enabled,
        autoReplyMessage: autoReply.message,
        brandColor: bizInfo.brandColor,
      };

      const result = await updateOrganization({
        organizationId,
        data: updateData
      }).unwrap();

      reduxLogger.info('Business settings saved successfully:', result);
      showToast.success('Business settings saved successfully');
    } catch (error: unknown) {
      reduxLogger.error('Failed to save business settings:', error);

      const errors: Record<string, string> = {};
      const messages: string[] = [];

      if (isRtkQueryError(error) && error.data?.error?.details) {
        const details = error.data.error.details as Array<{ field?: string; message: string }>;
        for (const detail of details) {
          if (detail.field) {
            errors[detail.field] = detail.message;
          }
          messages.push(detail.message);
        }
      }

      setFieldErrors(errors);
      showToast.error(
        'Failed to save business settings',
        messages.length > 0 ? messages.join('. ') : 'Please try again.',
      );
    }
  };

  if (isLoadingOrganization) {
    return (
      <div className="max-w-6xl mx-auto space-y-8">
        <YourBusinessHeader />
        <div className="bg-card-background rounded-lg border border-border p-6">
          <Skeleton className="h-6 w-48 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i}>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (organizationError) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
              <X className="h-8 w-8 text-error" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">Failed to load business data</h3>
            <p className="text-sm text-text-secondary">Please refresh the page or contact support if the issue persists.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <YourBusinessHeader />

      <BusinessInfoSection
        companyName={bizInfo.companyName}
        companyEmail={bizInfo.companyEmail}
        companyWebsite={bizInfo.companyWebsite}
        companyAddress={bizInfo.companyAddress}
        brandColor={bizInfo.brandColor}
        themePrimary={theme.primary}
        fieldErrors={fieldErrors}
        onCompanyNameChange={(v) => setBizInfo({ companyName: v })}
        onCompanyEmailChange={(v) => setBizInfo({ companyEmail: v })}
        onCompanyWebsiteChange={(v) => setBizInfo({ companyWebsite: v })}
        onCompanyAddressChange={(v) => setBizInfo({ companyAddress: v })}
        onBrandColorChange={(v) => setBizInfo({ brandColor: v })}
      />

      <TimezoneSection
        timezone={bizInfo.timezone}
        themePrimary={theme.primary}
        fieldErrors={fieldErrors}
        onTimezoneChange={(v) => setBizInfo({ timezone: v })}
      />

      <BusinessHoursSection
        businessHours={businessHours}
        themePrimary={theme.primary}
        themeBorder={theme.border}
        onToggleDay={handleToggleDay}
        onTimeChange={handleTimeChange}
      />

      <AfterHoursSection
        autoReplyEnabled={autoReply.enabled}
        autoReplyMessage={autoReply.message}
        themePrimary={theme.primary}
        themeBorder={theme.border}
        onToggleAutoReply={() => setAutoReply(prev => ({ ...prev, enabled: !prev.enabled }))}
        onAutoReplyMessageChange={(msg) => setAutoReply(prev => ({ ...prev, message: msg }))}
      />

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isUpdatingOrganization}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{
            backgroundColor: theme.primary,
            '--tw-ring-color': theme.primary
          } as React.CSSProperties}
        >
          {isUpdatingOrganization ? (
            <>
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  );
};

export default YourBusiness;
